import * as React from 'react';

import type { PurchaseLoadStatus } from '@rematter/frontend-lib-api-client';
import { enumPurchaseLoadStatus } from '@rematter/frontend-lib-api-client';
import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  cn,
} from '@rematter/ui-web';
import { LucideAlertTriangle, LucideLoader2 } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';
import {
  STEP_ORDER,
  useStepperTransition,
} from '../hooks/useStepperTransition';

type ScaleTicketStatusStepperProps = {
  ticketId: string | null | undefined;
  status: PurchaseLoadStatus;
  isProcessing: boolean;
  onTransitioningChange?: (isTransitioning: boolean) => void;
  facilityId?: string;
};

const STEP_KEYS: Partial<Record<PurchaseLoadStatus, string>> = {
  Open: 'open',
  InventoryComplete: 'inventoryComplete',
  PriceComplete: 'priceComplete',
  Paid: 'paid',
};

const STATUS_INDEX = Object.fromEntries(
  STEP_ORDER.map((status, index) => [status, index]),
) as Record<string, number>;

//
// ⚛️ ⚛️ ⚛️ SCALE TICKET STATUS STEPPER ⚛️ ⚛️ ⚛️
//
export const ScaleTicketStatusStepper = ({
  ticketId,
  status,
  isProcessing,
  onTransitioningChange,
  facilityId,
}: ScaleTicketStatusStepperProps) => {
  const t = useTranslations();

  const {
    transitionTo,
    isTransitioning,
    isRevertDialogOpen,
    pendingRevertTarget,
    confirmRevert,
    cancelRevert,
  } = useStepperTransition({
    ticketId,
    currentStatus: status,
  });

  React.useEffect(() => {
    onTransitioningChange?.(isTransitioning);
  }, [isTransitioning, onTransitioningChange]);

  if (status === enumPurchaseLoadStatus.Voided) return null;

  const currentIndex = STATUS_INDEX[status] ?? -1;
  const isHeld = status === enumPurchaseLoadStatus.Held;
  const noFacility = !facilityId;
  const isGloballyDisabled =
    isProcessing || isTransitioning || isHeld || noFacility;

  const stepLabels: Record<string, string> = {
    open: t.scaleTicketPro.stepper.open,
    inventoryComplete: t.scaleTicketPro.stepper.inventoryComplete,
    priceComplete: t.scaleTicketPro.stepper.priceComplete,
    paid: t.scaleTicketPro.stepper.paidBadge,
  };

  const revertTargetLabel = pendingRevertTarget
    ? (stepLabels[STEP_KEYS[pendingRevertTarget] ?? ''] ?? '')
    : '';

  return (
    <div
      className="@ScaleTicketStatusStepper relative"
      aria-label="Scale ticket status stepper"
    >
      <div
        className={cn(
          'flex items-center gap-1',
          isHeld && 'pointer-events-none opacity-60',
        )}
      >
        {STEP_ORDER.map((stepStatus, index) => {
          const isCurrent = !isHeld && index === currentIndex;
          const isPaidStep = stepStatus === 'Paid';
          const isStepDisabled = isGloballyDisabled || isPaidStep;

          return (
            <button
              key={stepStatus}
              type="button"
              disabled={isStepDisabled}
              onClick={() => transitionTo(stepStatus)}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                isCurrent &&
                  'bg-primary/10 text-primary ring-1 ring-primary ring-inset',
                !isCurrent && !isStepDisabled && 'text-foreground/70',
                !isCurrent &&
                  isStepDisabled &&
                  'cursor-default text-foreground/30',
              )}
            >
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                  isCurrent && 'bg-primary text-white',
                  !isCurrent &&
                    !isStepDisabled &&
                    'bg-foreground/15 text-foreground/60',
                  !isCurrent &&
                    isStepDisabled &&
                    'bg-foreground/5 text-foreground/30',
                )}
              >
                {isTransitioning && isCurrent ? (
                  <LucideLoader2 size={12} className="animate-spin" />
                ) : (
                  index + 1
                )}
              </span>
              {stepLabels[STEP_KEYS[stepStatus] ?? '']}
            </button>
          );
        })}
      </div>

      {isRevertDialogOpen && (
        <DialogOverlay
          isOpen
          isDismissable
          onOpenChange={(open) => !open && cancelRevert()}
          className="@ScaleTicketRevertConfirmDialog"
        >
          <DialogContent
            onOpenChange={(open: boolean) => !open && cancelRevert()}
          >
            <DialogHeader>
              <div className="flex items-center gap-2">
                <LucideAlertTriangle className="size-5 text-warning" />
                <span className="text-base font-semibold">
                  {t.scaleTicketPro.stepper.revertConfirmTitle}
                </span>
              </div>
            </DialogHeader>
            <div className="flex flex-col gap-2 px-4">
              <div className="text-base text-neutral/50">
                {t.scaleTicketPro.stepper.revertConfirmDescription(
                  revertTargetLabel,
                )}
              </div>
              <div className="flex justify-end gap-2 py-4">
                <Button variant="text" color="neutral" onPress={cancelRevert}>
                  {t.common.cancel}
                </Button>
                <Button variant="solid" color="failure" onPress={confirmRevert}>
                  {t.scaleTicketPro.stepper.revertConfirmButton}
                </Button>
              </div>
            </div>
          </DialogContent>
        </DialogOverlay>
      )}
    </div>
  );
};
